"use client";

import { useEffect, useState } from "react";
import { X, Phone } from "lucide-react";
import { generateCookieId, getClientIp } from "./lib/clientInfo";

type Agency = {
  id?: number | string;
  nom?: string;
  telephone?: string;
  slug?: string;
};

export default function CallBack({
  onClose,
  agency,
}: {
  onClose: () => void;
  agency: Agency;
}) {
  const [submitted, setSubmitted] = useState(false);
  const [phone, setPhone] = useState("");
  const [firstname, setFirstname] = useState("");
  const [lastname, setLastname] = useState("");
  const [cookieId] = useState(generateCookieId());
  const [ip, setIp] = useState("0.0.0.0");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    getClientIp().then((v) => v && setIp(v));
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError("");

    const idelioUrl = process.env.NEXT_PUBLIC_IDELIO_API_URL;
    const strapiUrl = process.env.NEXT_PUBLIC_STRAPI_ETRE_RAPPELES_ENDPOINT;

    if (!idelioUrl) {
      setError("L’URL de l’API Idelio est introuvable.");
      return;
    }

    if (!strapiUrl) {
      setError("L’URL de l’endpoint Strapi est introuvable.");
      return;
    }

    if (!phone.trim()) {
      setError("Veuillez saisir votre numéro de téléphone.");
      return;
    }

    if (!agency?.telephone) {
      setError("Le numéro de l’agence est introuvable.");
      return;
    }

    try {
      setLoading(true);

      const idelioPayload = {
        agenceId: Number(agency.id),
        prospect: {
          pays: "FR",
          numero: phone,
          ip,
          cookie: cookieId,
          nom: lastname,
          prenom: firstname,
        },
      };

      const strapiPayload = {
        data: {
          firstname,
          lastname,
          userPhone: phone,
          agencyPhone: agency.telephone,
          agencyName: agency.nom,
          agencySlug: agency.slug,
        },
      };

      const [idelioResponse, strapiResponse] = await Promise.all([
        fetch(idelioUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(idelioPayload),
        }),
        fetch(strapiUrl, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(strapiPayload),
        }),
      ]);

      if (!idelioResponse.ok) {
        const idelioError = await idelioResponse.text();
        console.error("Erreur Idelio :", idelioError);
        throw new Error("Échec de l’appel API Idelio");
      }

      if (!strapiResponse.ok) {
        const strapiError = await strapiResponse.text();
        console.error("Erreur Strapi :", strapiError);
        throw new Error("Échec de l’enregistrement dans Strapi");
      }

      setSubmitted(true);
      setPhone("");
    } catch (err) {
      console.error("Erreur callback :", err);
      setError("Une erreur est survenue lors de l’envoi de la demande.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 px-4 backdrop-blur-sm">
      <div className="relative w-full max-w-2xl rounded-xl bg-white p-8 shadow-xl">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-4 top-4 text-gray-500 transition hover:text-black"
        >
          <X size={26} />
        </button>

        <h2 className="mb-6 text-center text-2xl font-semibold">
          Être rappelé
        </h2>

        {agency?.nom && (
          <p className="mb-4 text-center text-sm text-gray-600">
            Agence : <span className="font-semibold">{agency.nom}</span>
          </p>
        )}

        {!submitted ? (
          <form className="space-y-6" onSubmit={handleSubmit}>
            {/* Nom / Prénom */}

            <div className="flex gap-4">
              <div className="w-1/2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Nom <span className="text-pink">*</span></label>
                <input
                  type="text"
                  value={lastname}
                  onChange={(e) => setLastname(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primaryGreen"
                  required
                />
              </div>

              <div className="w-1/2">
                <label className="block text-sm font-medium text-gray-700 mb-1">Prénom <span className="text-pink">*</span></label>
                <input
                  type="text"
                  value={firstname}
                  onChange={(e) => setFirstname(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primaryGreen"
                  required
                />
              </div>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">
                Téléphone <span className="text-pink">*</span>
              </label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Votre numéro de téléphone"
                className="w-full rounded-lg border border-gray-300 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primaryGreen"
                required
              />
            </div>

            {error && <p className="text-sm text-red-500">{error}</p>}

            <button
              type="submit"
              disabled={loading}
              className="flex w-full justify-center gap-2 rounded-lg bg-pink py-3 text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              <Phone size={18} />
              {loading ? "ENVOI..." : "Être rappelé"}
            </button>
          </form>
        ) : (
          <p className="text-center text-pink">
            Merci ! Votre demande de rappel a bien été envoyée.
          </p>
        )}
      </div>
    </div>
  );
}